import { useState } from 'react'
import {
  completeReturn,
  endSubscription,
  pauseSubscription,
  reactivateSubscription,
  resumeSubscription,
  terminateSubscription,
  withdrawSubscription
} from '@/api/customers'
import { removeShipment } from '@/api/shipments'
import { useAuthStore } from '@/store/authStore'
import { getApiErrorMessage } from '@/lib/apiError'
import { hasPermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SubscriptionStatusBadge } from '@/components/SubscriptionStatusBadge'
import { FinalDeliveryPanel } from '@/components/customer/FinalDeliveryPanel'
import {
  canForceEndSubscription,
  canPauseSubscription,
  canReactivateSubscription,
  FORCE_END_CONFIRM_MESSAGE,
  FORCE_END_LABEL,
  getCancellationOptions,
  getFinalSequence,
  getHighestDeliverySequence,
  getRemoveUnsentOnPauseMessage,
  getSubscriptionAction,
  getUnsentShipments,
  getWithdrawalOptions,
  isSubscriptionPaused,
  PAUSE_CONFIRM_MESSAGE,
  PAUSE_LABEL,
  REACTIVATE_CONFIRM_MESSAGE,
  REACTIVATE_LABEL,
  RESUME_CONFIRM_MESSAGE,
  RESUME_LABEL,
  SUBSCRIPTION_ACTION_LABELS
} from '@/lib/subscription'
import type { Shipment, Subscription } from '@/api/types'

const WITHDRAWAL_QUESTION = 'Hur vill kunden ångra sig?'
const WITHDRAWAL_GROUP_LABEL = 'Typ av ånger'
const WITHDRAWAL_CONFIRM_LABEL = 'Bekräfta ånger'
const CANCELLATION_QUESTION = 'Vilken blir kundens sista leverans?'
const CANCELLATION_GROUP_LABEL = 'Sista leverans'
const CANCELLATION_CONFIRM_LABEL = 'Bekräfta uppsägning'
const COMPLETE_RETURN_CONFIRM_MESSAGE =
  'Har returen tagits emot? Fakturan makuleras hos Capway. Har kunden redan betalat något måste det återbetalas för hand.'
const CANCEL_ERROR_MESSAGE = 'Kunde inte säga upp abonnemanget just nu.'
const WITHDRAW_ERROR_MESSAGE = 'Kunde inte ångra abonnemanget just nu. Försök igen.'
const COMPLETE_RETURN_ERROR_MESSAGE = 'Kunde inte makulera fakturan just nu. Försök igen.'
const FORCE_END_ERROR_MESSAGE = 'Kunde inte avsluta abonnemanget just nu. Försök igen.'
const REACTIVATE_ERROR_MESSAGE = 'Kunde inte aktivera abonnemanget igen just nu. Försök igen.'
const PAUSE_ERROR_MESSAGE = 'Kunde inte pausa abonnemanget just nu. Försök igen.'
const RESUME_ERROR_MESSAGE = 'Kunde inte återuppta abonnemanget just nu. Försök igen.'
const REMOVE_UNSENT_ERROR_MESSAGE = 'Abonnemanget är pausat, men den bokade leveransen kunde inte tas bort. Ta bort den under Leveranser.'

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('sv-SE')
}

interface SubscriptionCardProps {
  customerId: number
  subscription: Subscription | null
  shipments: Shipment[]
  startedAt: string
  onSubscriptionChanged: (subscription: Subscription) => void
  onWithdrawalChanged: () => void
}

export function SubscriptionCard({
  customerId,
  subscription,
  shipments,
  startedAt,
  onSubscriptionChanged,
  onWithdrawalChanged
}: SubscriptionCardProps): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const canManageSubscriptions = hasPermission(user, 'subscriptions.manage')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFinalDeliveryPanelOpen, setIsFinalDeliveryPanelOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const action = subscription ? getSubscriptionAction(subscription.display_status) : null
  const canAct = action !== null && canManageSubscriptions && !isFinalDeliveryPanelOpen
  const canForceEnd = subscription !== null && canForceEndSubscription(subscription.display_status) && canManageSubscriptions
  const canReactivate = subscription !== null && canReactivateSubscription(subscription.display_status) && canManageSubscriptions
  const canPause = subscription !== null && canPauseSubscription(subscription) && canManageSubscriptions
  const isPaused = subscription !== null && isSubscriptionPaused(subscription)
  const pausedAt = subscription?.paused_at ?? null
  const canResume = isPaused && canManageSubscriptions
  const isWithdrawalPanelVisible = isFinalDeliveryPanelOpen && action === 'withdraw' && canManageSubscriptions
  const isCancellationPanelVisible = isFinalDeliveryPanelOpen && action === 'cancel' && canManageSubscriptions
  const highestSequence = getHighestDeliverySequence(shipments)
  const finalSequence = subscription ? getFinalSequence(subscription) : null
  const hasFinalSequence = finalSequence !== null
  const hasError = errorMessage !== null

  const runAction = async (request: () => Promise<Subscription>, failureMessage: string): Promise<boolean> => {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const updated = await request()

      onSubscriptionChanged(updated)
      setIsFinalDeliveryPanelOpen(false)
      onWithdrawalChanged()

      return true
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, failureMessage))

      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCompleteReturn = async (): Promise<void> => {
    if (!window.confirm(COMPLETE_RETURN_CONFIRM_MESSAGE)) {
      return
    }

    await runAction(() => completeReturn(customerId), COMPLETE_RETURN_ERROR_MESSAGE)
  }

  const handleWithdraw = async (selectedFinalSequence: number | null): Promise<void> => {
    await runAction(() => withdrawSubscription(customerId, selectedFinalSequence), WITHDRAW_ERROR_MESSAGE)
  }

  const handleTerminate = async (selectedFinalSequence: number | null): Promise<void> => {
    await runAction(() => terminateSubscription(customerId, selectedFinalSequence), CANCEL_ERROR_MESSAGE)
  }

  const handleActionClick = async (): Promise<void> => {
    setErrorMessage(null)

    if (action === 'withdraw' || action === 'cancel') {
      setIsFinalDeliveryPanelOpen(true)
      return
    }

    if (action === 'complete_return') {
      await handleCompleteReturn()
    }
  }

  const handleForceEnd = async (): Promise<void> => {
    if (!window.confirm(FORCE_END_CONFIRM_MESSAGE)) {
      return
    }

    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      const updated = await endSubscription(customerId)

      onSubscriptionChanged(updated)
      onWithdrawalChanged()
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, FORCE_END_ERROR_MESSAGE))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReactivate = async (): Promise<void> => {
    if (!window.confirm(REACTIVATE_CONFIRM_MESSAGE)) {
      return
    }

    await runAction(() => reactivateSubscription(customerId), REACTIVATE_ERROR_MESSAGE)
  }

  const removeUnsentAfterPause = async (): Promise<void> => {
    for (const shipment of getUnsentShipments(shipments)) {
      if (!window.confirm(getRemoveUnsentOnPauseMessage(shipment))) continue

      await removeShipment(shipment.id)
    }
  }

  const handlePause = async (): Promise<void> => {
    if (!window.confirm(PAUSE_CONFIRM_MESSAGE)) {
      return
    }

    const isPausedNow = await runAction(() => pauseSubscription(customerId), PAUSE_ERROR_MESSAGE)

    if (!isPausedNow) {
      return
    }

    try {
      await removeUnsentAfterPause()
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error, REMOVE_UNSENT_ERROR_MESSAGE))
    }

    onWithdrawalChanged()
  }

  const handleResume = async (): Promise<void> => {
    if (!window.confirm(RESUME_CONFIRM_MESSAGE)) {
      return
    }

    await runAction(() => resumeSubscription(customerId), RESUME_ERROR_MESSAGE)
  }

  const handleFinalDeliveryPanelClose = (): void => {
    setIsFinalDeliveryPanelOpen(false)
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader>
        <CardTitle>Abonnemang</CardTitle>
        <CardDescription>Status och livscykel för kundens abonnemang.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <SubscriptionStatusBadge subscription={subscription} />
          <div className="flex flex-wrap justify-end gap-2">
            {canResume && (
              <Button
                variant="default"
                className="rounded-none"
                disabled={isSubmitting}
                onClick={handleResume}
              >
                {RESUME_LABEL}
              </Button>
            )}
            {canPause && (
              <Button
                variant="outline"
                className="rounded-none"
                disabled={isSubmitting}
                onClick={handlePause}
              >
                {PAUSE_LABEL}
              </Button>
            )}
            {canReactivate && (
              <Button
                variant="outline"
                className="rounded-none"
                disabled={isSubmitting}
                onClick={handleReactivate}
              >
                {REACTIVATE_LABEL}
              </Button>
            )}
            {canForceEnd && (
              <Button
                variant="outline"
                className="rounded-none"
                disabled={isSubmitting}
                onClick={handleForceEnd}
              >
                {FORCE_END_LABEL}
              </Button>
            )}
            {canAct && (
              <Button
                variant="destructive"
                className="rounded-none"
                disabled={isSubmitting}
                onClick={handleActionClick}
              >
                {SUBSCRIPTION_ACTION_LABELS[action]}
              </Button>
            )}
          </div>
        </div>

        {isWithdrawalPanelVisible && (
          <FinalDeliveryPanel
            question={WITHDRAWAL_QUESTION}
            groupLabel={WITHDRAWAL_GROUP_LABEL}
            confirmLabel={WITHDRAWAL_CONFIRM_LABEL}
            options={getWithdrawalOptions(highestSequence)}
            isSubmitting={isSubmitting}
            onConfirm={handleWithdraw}
            onCancel={handleFinalDeliveryPanelClose}
          />
        )}

        {isCancellationPanelVisible && (
          <FinalDeliveryPanel
            question={CANCELLATION_QUESTION}
            groupLabel={CANCELLATION_GROUP_LABEL}
            confirmLabel={CANCELLATION_CONFIRM_LABEL}
            options={getCancellationOptions(highestSequence)}
            isSubmitting={isSubmitting}
            onConfirm={handleTerminate}
            onCancel={handleFinalDeliveryPanelClose}
          />
        )}

        {subscription && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-muted-foreground">Startad</p>
              <p>{formatDate(startedAt)}</p>
            </div>
            {subscription.withdrawn_at && (
              <div>
                <p className="text-sm text-muted-foreground">Ångrad</p>
                <p>{formatDate(subscription.withdrawn_at)}</p>
              </div>
            )}
            {subscription.cancelled_at && (
              <div>
                <p className="text-sm text-muted-foreground">Uppsagd</p>
                <p>{formatDate(subscription.cancelled_at)}</p>
              </div>
            )}
            {pausedAt && (
              <div>
                <p className="text-sm text-muted-foreground">Pausat</p>
                <p>{formatDate(pausedAt)}</p>
              </div>
            )}
            {hasFinalSequence && (
              <div>
                <p className="text-sm text-muted-foreground">Sista leverans</p>
                <p>Leverans {finalSequence}</p>
              </div>
            )}
          </div>
        )}

        {hasError && <p className="text-sm text-destructive">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
